// ** Third Party Imports
import { fetchBaseQuery } from '@reduxjs/toolkit/query'
import { Mutex } from 'async-mutex'

// ** Lib Imports
import { env } from '../lib'

const baseQuery = fetchBaseQuery({
	baseUrl: env.apiUrl,
	credentials: 'include' // ensures cookies are sent
})

// mutex to prevent multiple simultaneous refresh token requests
const mutex = new Mutex()

// enhanced baseQuery that handles 401 errors by attempting token refresh
export const baseQueryWithReauth: typeof baseQuery = async (
	args,
	api,
	extraOptions
) => {
	// wait if another refresh token request is in progress
	await mutex.waitForUnlock()

	// attempt the original API request
	let result = await baseQuery(args, api, extraOptions)

	// if the request failed with a 401 (unauthorized), attempt reauthentication
	if (result.error && result.error.status === 401) {
		// only one refresh request should be sent at a time
		if (!mutex.isLocked()) {
			const release = await mutex.acquire()

			try {
				// try refreshing the token
				const refreshResult = await baseQuery(
					{
						url: '/auth/refresh',
						method: 'POST'
					},
					api,
					extraOptions
				)

				if (refreshResult.error) {
					await baseQuery(
						{ url: '/auth/logout', method: 'POST' },
						api,
						extraOptions
					)

					// return the error so your app can redirect to login
					return refreshResult
				} else {
					// retry the original request after successful token refresh
					result = await baseQuery(args, api, extraOptions)
				}
			} finally {
				// always release the mutex lock after the refresh logic completes
				release()
			}
		} else {
			// if another refresh request is already in progress, wait for it to finish
			await mutex.waitForUnlock()
			// retry the original request after the token is refreshed
			result = await baseQuery(args, api, extraOptions)
		}
	}

	// return the final result (either original, retried, or refresh error)
	return result
}
